import {
  loginResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  registerResponseSchema,
  type AuthUserSummary,
  type LoginRequest,
  type RegisterRequest,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ApiRequestError, apiGet, apiPost } from '@/lib/api';
import {
  applyTokens,
  clearSession,
  loadPersistedSession,
  session,
  setSessionUser,
} from '@/lib/session';

/**
 * Authentication state.
 *
 * The tokens themselves live in `lib/session.ts` (see the note there on why they
 * are outside Pinia); this store owns the user profile, the request status and the
 * three flows the UI drives. Logging out clears the local session even when the
 * server call fails — a user who pressed "Đăng xuất" must end up logged out of
 * this browser regardless of what the network did.
 */

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUserSummary | null>(null);
  const submitting = ref(false);
  const error = ref<ApiRequestError | null>(null);
  const bootstrapped = ref(false);

  const isAuthenticated = computed(() => session.accessToken !== null);
  const displayName = computed(() => user.value?.displayName ?? user.value?.email ?? '');

  /** Rehydrate from storage, then confirm the token still works. */
  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return;
    loadPersistedSession();
    user.value = session.user;

    if (session.accessToken) {
      try {
        await fetchMe();
      } catch {
        // `fetchMe` already cleared the session if the refresh chain failed; the
        // router guard sends the user to /login from here.
      }
    }

    bootstrapped.value = true;
  }

  async function fetchMe(): Promise<AuthUserSummary> {
    const response = await apiGet('/auth/me', meResponseSchema);
    user.value = response.user;
    setSessionUser(response.user);
    return response.user;
  }

  async function login(payload: LoginRequest): Promise<void> {
    submitting.value = true;
    error.value = null;
    try {
      /**
       * Login retries NETWORK_ERROR a few times before giving up. In dev the
       * API restarts for seconds every time its watch rebuilds, and a login
       * that lands in that window used to fail instantly with "kiểm tra API
       * có đang chạy không" — while the API was two seconds from answering.
       * Only this endpoint gets the courtesy: verifying credentials is
       * side-effect-free, so replaying it is safe, which is not true of the
       * app's other POSTs. Real failures (sai mật khẩu…) carry a response and
       * are never retried.
       */
      const loginWithRetry = async () => {
        for (let attempt = 0; ; attempt += 1) {
          try {
            return await apiPost('/auth/login', loginResponseSchema, payload);
          } catch (caught) {
            const retryable =
              caught instanceof ApiRequestError && caught.code === 'NETWORK_ERROR' && attempt < 3;
            if (!retryable) throw caught;
            await new Promise((resolve) => setTimeout(resolve, 1_500));
          }
        }
      };
      const response = await loginWithRetry();
      applyTokens(response.tokens);
      user.value = response.user;
      setSessionUser(response.user);
    } catch (caught) {
      error.value = caught instanceof ApiRequestError ? caught : null;
      throw caught;
    } finally {
      submitting.value = false;
    }
  }

  async function register(payload: RegisterRequest): Promise<{ emailVerificationRequired: boolean }> {
    submitting.value = true;
    error.value = null;
    try {
      const response = await apiPost('/auth/register', registerResponseSchema, payload);
      applyTokens(response.tokens);
      user.value = response.user;
      setSessionUser(response.user);
      return { emailVerificationRequired: response.emailVerificationRequired };
    } catch (caught) {
      error.value = caught instanceof ApiRequestError ? caught : null;
      throw caught;
    } finally {
      submitting.value = false;
    }
  }

  async function logout(allDevices = false): Promise<void> {
    const refreshToken = session.refreshToken;
    try {
      await apiPost('/auth/logout', logoutResponseSchema, {
        refreshToken: refreshToken ?? undefined,
        allDevices,
      });
    } catch {
      // Best effort: the local session is dropped either way.
    } finally {
      user.value = null;
      error.value = null;
      clearSession();
    }
  }

  /** Called when the refresh chain died under us; no server round-trip is left. */
  function forgetSession(): void {
    user.value = null;
    clearSession();
  }

  return {
    user,
    submitting,
    error,
    bootstrapped,
    isAuthenticated,
    displayName,
    bootstrap,
    fetchMe,
    login,
    register,
    logout,
    forgetSession,
  };
});
